/**
 * Narrows an unknown value to an indexable record. A type predicate rather than
 * a cast, so reading properties off it stays type-safe.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Narrows to a usable, non-blank string. */
function asText(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** Reads a string property off an unknown value without assuming its shape. */
function readText(source: unknown, key: string): string | undefined {
  return isRecord(source) ? asText(source[key]) : undefined;
}

/**
 * Extracts a human-readable message from a rejected RTK Query request.
 *
 * `.unwrap()` rejects with a `FetchBaseQueryError` or a `SerializedError` —
 * both **plain objects**, never `Error` instances. This is why the intuitive
 * `error instanceof Error ? error.message : 'Unknown error'` silently collapses
 * every backend error into the fallback: the check can never be true.
 *
 * Sources are tried from most to least specific:
 * 1. `data.message` — the structured JSON error body our APIs return;
 * 2. `data.error` — the same idea under an alternative key;
 * 3. `error` — RTK's transport-level string (`FETCH_ERROR`, `TIMEOUT_ERROR`,
 *    `PARSING_ERROR`, `CUSTOM_ERROR`). Deliberately ahead of a raw string
 *    `data`, because on a parse failure `data` holds the unparsed body (often a
 *    whole HTML error page) while `error` holds the actual reason;
 * 4. `data` when the body is plain text rather than JSON;
 * 5. `message` — a `SerializedError` (thrown inside a query fn, aborts, …);
 * 6. `status` — no message anywhere, but an HTTP status worth showing.
 *
 * @param error - the rejection value, as caught from `.unwrap()`.
 * @param fallback - returned when nothing usable is found.
 *
 * @example
 * ```ts
 * try {
 *   await createThing({ body }).unwrap();
 * } catch (error) {
 *   message.error(`Error creating thing: ${getQueryErrorMessage(error)}`);
 * }
 * ```
 */
export function getQueryErrorMessage(error: unknown, fallback = 'Unknown error'): string {
  if (!isRecord(error)) {
    return fallback;
  }

  const { data } = error;

  const message =
    readText(data, 'message') ??
    readText(data, 'error') ??
    asText(error.error) ??
    asText(data) ??
    asText(error.message);

  if (message !== undefined) {
    return message;
  }

  const { status } = error;
  if (typeof status === 'number' || asText(status) !== undefined) {
    return `Request failed with status ${String(status)}`;
  }

  return fallback;
}
